// Liquid supplies the versioned Shopify URL so browser/CDN caches refresh when
// the bounds helper changes, just as they do for this entry module.
const helperURL = document.querySelector('script[data-henry-image-framing]').dataset.boundsUrl;
const { findProductBounds, fitProductBounds } = await import(helperURL);

// A shared image enhancement leaves the existing ProductCard refs and events
// intact. It also covers cards inserted by filtering, search and recommendations,
// and every still image in desktop/mobile galleries. Related costumes need the
// same bounds correction as centred party supplies. Zoom keeps the original.
const gallerySelector = 'media-gallery .product-media-container--image:not(.dialog-zoomed-gallery *) .product-media__image';
const selector = `.product-card .card-gallery .product-media__image, .resource-card[data-resource-type="product"] .resource-card__image, .pd-related__product .pd-related__image, ${gallerySelector}`;
const frameSizes = new WeakMap();
const states = new WeakMap();
const cache = new Map();
const queue = new Set();
let scheduled = false;

const resize = new ResizeObserver((entries) => {
  for (const { target, contentRect } of entries) {
    // Observer measurements do not force a synchronous layout after style writes.
    frameSizes.set(target, { width: contentRect.width, height: contentRect.height });
    for (const image of target.querySelectorAll('img')) applyFrame(image);
  }
});

const visibility = new IntersectionObserver((entries) => {
  for (const { target, isIntersecting } of entries) {
    const state = states.get(target);
    if (!state) continue;
    state.visible = isIntersecting;
    if (isIntersecting) enqueue(target);
  }
}, { rootMargin: '350px' });

function applyFrame(image) {
  const state = states.get(image);
  if (!state?.bounds || !image.isConnected || !image.naturalWidth) return;
  const size = frameSizes.get(state.frame);
  if (!size?.width || !size.height) return;
  const inGallery = image.matches(gallerySelector);
  const alignment = inGallery ? 'center' : image.closest('[data-henry-image-alignment]')?.dataset.henryImageAlignment;
  const fitKey = `${state.source}|${size.width}|${size.height}|${alignment}`;
  if (state.fitKey === fitKey && image.hasAttribute('data-henry-framed')) return;
  state.fitKey = fitKey;
  const values = fitProductBounds(state.bounds, image.naturalWidth / image.naturalHeight, size.width / size.height, alignment);
  // A consistent 2% safety inset retains hats, shoes, props and soft outlines.
  if (inGallery) {
    values.width *= 0.96;
    values.height *= 0.96;
    values.left = 2 + values.left * 0.96;
    values.top = 2 + values.top * 0.96;
  }
  for (const [name, value] of Object.entries(values)) image.style.setProperty(`--henry-image-${name}`, `${value}%`);
  image.dataset.henryFramed = '';
}

function enqueue(image) {
  const state = states.get(image);
  if (!state || !image.complete || !image.naturalWidth) return;
  // Prepare photos just before they enter view, yielding between analyses.
  if (!state.visible) return;
  queue.add(image);
  if (scheduled) return;
  scheduled = true;
  // Spread analysis across tasks; use already loaded pixels without additional
  // image requests. Never delay the original photo's first paint.
  scheduleAnalysis();
}

function scheduleAnalysis() {
  if ('requestIdleCallback' in window) window.requestIdleCallback(processQueue, { timeout: 1500 });
  else setTimeout(processQueue, 32);
}

function processQueue() {
  scheduled = false;
  const image = queue.values().next().value;
  if (!image) return;
  queue.delete(image);
  processImage(image);
  if (queue.size) {
    scheduled = true;
    scheduleAnalysis();
  }
}

function processImage(image) {
  const state = states.get(image);
  if (state && image.isConnected && image.complete && image.naturalWidth) {
    const source = image.currentSrc || image.src;
    if (state.source !== source) {
      state.source = source;
      state.bounds = null;
      state.fitKey = '';
      delete image.dataset.henryFramed;
      if (!cache.has(source)) {
        let bounds = null;
        try {
          const canvas = document.createElement('canvas');
          const ratio = Math.min(1, 400 / Math.max(image.naturalWidth, image.naturalHeight));
          canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
          canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          bounds = findProductBounds(context.getImageData(0, 0, canvas.width, canvas.height));
        } catch {
          // Cross-origin or unreadable images keep the entire original photo.
        }
        if (cache.size >= 200) cache.delete(cache.keys().next().value);
        cache.set(source, bounds);
      }
      state.bounds = cache.get(source);
    }
    applyFrame(image);
  }
}

function register(image) {
  if (states.has(image)) {
    enqueue(image);
    return;
  }
  const frame = image.closest('.product-media, .resource-card__media, .pd-related__media');
  if (!frame) return;
  states.set(image, { frame, visible: false, source: '', bounds: null });
  visibility.observe(image);
  resize.observe(frame);
}

function visit(root, callback) {
  if (!(root instanceof Element || root instanceof Document)) return;
  if (root instanceof HTMLImageElement && root.matches(selector)) callback(root);
  for (const image of root.querySelectorAll(selector)) callback(image);
}

function init() {
  visit(document, register);
  document.addEventListener('load', (event) => {
    if (event.target instanceof HTMLImageElement && event.target.matches(selector)) register(event.target);
  }, { capture: true });

  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') {
        const image = record.target;
        if (record.attributeName === 'data-henry-framed') {
          // Filtering can reuse a loaded image while removing its client-only
          // framing. Restore the cached fit even when its source/size is unchanged.
          // Ignore our own marker writes and never reuse bounds for a new photo.
          const state = states.get(image);
          if (!image.hasAttribute('data-henry-framed') && image.complete &&
              state?.source === (image.currentSrc || image.src)) applyFrame(image);
          continue;
        }
        if (image.matches(selector)) register(image);
        continue;
      }
      for (const node of record.removedNodes) {
        if (!(node instanceof Element)) continue;
        const images = node instanceof HTMLImageElement ? [node] : node.querySelectorAll('img');
        for (const image of images) {
          if (image.isConnected) continue;
          const state = states.get(image);
          visibility.unobserve(image);
          if (state) resize.unobserve(state.frame);
          queue.delete(image);
          states.delete(image);
        }
      }
      for (const node of record.addedNodes) visit(node, register);
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'data-henry-framed'] });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
